import {ChangeDetectionStrategy, Component} from '@angular/core';
import {SystemCode} from '@helpers/types/models';
import {UserService} from '../../../auth/user.service';
import {
	AlarisComplexTableComponent,
	AlarisDialogService,
	AlarisTableStateService,
	EditPanelWidth,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR
} from '@campaign-portal/components-library';
import {TableSettingsService} from '@helpers/api/table-settings.service';
import {TableTypes} from '@campaign-portal/namespace/entities/table-settings/specs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FilterType, Paging} from '@campaign-portal/namespace/common/rpc.params';
import {SystemCodesService} from './system-codes.service';
import {SystemCodesFieldsService} from './system-codes-fields.service';
import {EditSystemCodeComponent} from './edit-code/edit-code.component';
import {SystemCodesDialogComponent} from './dialog/dialog.component';

@Component({
	selector: 'app-system-codes',
	templateUrl: './system-codes.component.html',
	styleUrls: [
		'../../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
		'./system-codes.component.scss'
	],
	providers: [
		AlarisTableStateService,
		{provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService}
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SystemCodesComponent extends AlarisComplexTableComponent<SystemCode> {
	override rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-edit',
			label: 'actions.edit',
			action: this.edit.bind(this),
			disabled: (code: SystemCode): boolean => {
				return !this.user.allowed(['system_codes.edit']) || code.type === 'default'
			}
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (code: SystemCode): boolean => {
				return !this.user.allowed(['system_codes.edit']) || code.type === 'default'
			}
		}
	];
	override readonly dblClickRowAction = this.edit;
	override readonly pageSizeOptions = [25, 50, 100, 200];
	override readonly paging: Paging = {
		Skip: this.activePage,
		Take: this.pageSize
	};

	constructor(
		private readonly dialog: AlarisDialogService,
		public override readonly fieldsService: SystemCodesFieldsService,
		public readonly service: SystemCodesService,
		public readonly user: UserService,
		dataService: SystemCodesService,
		tableSettingsService: TableSettingsService
	) {
		super(
			dataService,
			fieldsService,
			tableSettingsService,
			TableTypes.INTERNAL
		);
		this.state.filters()
			.set(this.fieldsService.id.variable, {enabled: false, filterType: FilterType.LIKE})
			.set(this.fieldsService.codeValue.variable, {enabled: true, filterType: FilterType.LIKE})
			.set(this.fieldsService.type.variable, {enabled: true, filterType: FilterType.LIKE})
			.set(this.fieldsService.codeDescription.variable, {enabled: false, filterType: FilterType.LIKE})
			.set(this.fieldsService.lastUpdated.variable, {enabled: false, filterType: FilterType.LIKE})
	}

	edit(code?: SystemCode): void {
		if (this.user.allowed(['system_codes.edit']) && code?.type !== 'default') {
			this.editPanel.open<EditSystemCodeComponent, {
				client: SystemCode
			}, boolean>(EditSystemCodeComponent, EditPanelWidth.SM, {code})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((result) => {
					if (result) {
						this.load();
					}
				});
		}
	}

	delete(code: SystemCode): void {
		this.service.getRelatedTranslationRules(code?.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				if (resp.translationRulesList) {
					this.dialog.open<boolean>(SystemCodesDialogComponent,
						{autoFocus: false, data: {code: code, rules: resp.translationRulesList}})
						.closed
						.pipe(takeUntilDestroyed(this.destroyRef))
						.subscribe((result) => {
							if (result) {
								this.load();
							}
						});
				}
			}
			);
	}

	formatDate(inputDate: string | undefined): string {
		if (inputDate) {
			const date = new Date(inputDate);
			const pad = (num: number): string => num.toString().padStart(2, '0');

			const day = pad(date.getDate());
			const month = pad(date.getMonth() + 1);
			const year = date.getFullYear();

			const hours = pad(date.getHours());
			const minutes = pad(date.getMinutes());
			const seconds = pad(date.getSeconds());

			return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
		} else {
			return '';
		}
	}
}
